import React, { useCallback, useMemo, useState } from 'react';
import { FilterList, CloseOutlined } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { useTranslation } from 'react-i18next';
import TitledDrawer from '../../../../components/TitledDrawer';

interface Metric {
  name: string;
  description: string;
}

interface MetricsDashboardPanelProps {
  metrics: Metric[];
  onClose: () => void;
}

const sortMetrics = (metrics: Metric[]) => [...metrics].sort((metric1, metric2) => {
    const name1 = metric1.name.toUpperCase();
    const name2 = metric2.name.toUpperCase();
    return name1 < name2 ? -1 : 1;
});


const MetricsDashboardPanel = ({ metrics, onClose }: MetricsDashboardPanelProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<number[]>([]);
  const sortedMetrics = useMemo(() => sortMetrics(metrics), [metrics, sortMetrics]);

  const fireResize = useCallback(() => {
    if (typeof Event === 'function') {
      // modern browsers
      window.dispatchEvent(new Event('resize'));
    } else {
      // for IE and other old browsers
      // causes deprecation warning on modern browsers
      const evt = window.document.createEvent('UIEvents');
      evt.initUIEvent('resize', true, false, window, 0);
      window.dispatchEvent(evt);
    }
  }, []);

  return (
    <TitledDrawer
      title={t('screens.dashboarding.metricsDefinitions')}
      bottomToolbarMarginTop='16px'
      icon={<FilterList
        sx={{
          color: 'text.secondary',
        }}
      />}
      iconRight={
        <CloseOutlined
        />
      }
      onClose={onClose}
    >
      <Grid
        item
        sx={{
          paddingLeft: '4px',
          paddingRight: '4px',
          paddingTop: '10px'
        }}
      >
        {sortedMetrics.map((metric, index) => {
          const isExpanded = expanded.includes(index);

          return (
            <React.Fragment key={metric.name}>
              <Accordion
                expanded={isExpanded}
                elevation={0}
                disableGutters
                square
                sx={{
                  padding: 0,
                  border: 0,
                  margin: 0,
                }}
                onChange={(_, isOpen) => {
                  setExpanded(prev =>
                    isOpen
                      ? [...prev, index]
                      : prev.filter(item => item !== index)
                  );

                  setTimeout(fireResize, 250);
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`metric-${index}-content`}
                  sx={{ padding: 0, border: 0, margin: 0 }}
                  id={`metric-${index}-header`}
                >
                  <Typography variant="h3">
                    {metric.name}
                  </Typography>
                </AccordionSummary>

                <AccordionDetails sx={{ padding: 0, border: 0, margin: 0 }}>
                  <Typography>
                    {metric.description}
                  </Typography>
                </AccordionDetails>
              </Accordion>

              {isExpanded && (
                <Divider sx={{ marginTop: '16px', marginBottom: '4px' }} />
              )}
            </React.Fragment>
          );
        })}
      </Grid>
    </TitledDrawer>
  );
};

export default MetricsDashboardPanel;
